import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { checkUsername } from '@/shared/api/auth';

/** What is known about a username being typed. */
export type UsernameState = 'idle' | 'checking' | 'available' | 'taken' | 'reserved' | 'invalid';

const pattern = /^[A-Za-z0-9]([A-Za-z0-9-]{0,37}[A-Za-z0-9])?$/;

/** Whether a username has the right shape; mirrors the server rule. */
export function isUsernameShapeValid(username: string): boolean {
  return pattern.test(username) && !username.includes('--');
}

/**
 * Checks a username while it is being typed: locally for its shape at once,
 * on the server for availability after a pause, so that not every keystroke
 * costs a request.
 */
export function useUsernameCheck(username: string, current?: string): { state: UsernameState } {
  const [debounced, setDebounced] = useState(username);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(username);
    }, 400);

    return () => {
      clearTimeout(timer);
    };
  }, [username]);

  const unchanged = username.toLowerCase() === current?.toLowerCase();
  const shapeValid = isUsernameShapeValid(username);

  const query = useQuery({
    queryKey: ['username-available', debounced.toLowerCase()],
    queryFn: ({ signal }) => checkUsername(debounced, signal),
    enabled: shapeValid && !unchanged && debounced === username,
    staleTime: 10_000,
  });

  if (username === '' || unchanged) {
    return { state: 'idle' };
  }

  if (!shapeValid) {
    return { state: 'invalid' };
  }

  if (debounced !== username || query.isFetching || !query.data) {
    return { state: 'checking' };
  }

  return { state: query.data.available ? 'available' : (query.data.reason ?? 'taken') };
}
